import React, { useState, useEffect, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, TextField, Typography, Menu, MenuItem, IconButton, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonOutlineSharpIcon from '@mui/icons-material/PersonOutlineSharp';
import './DataTable.scss';
import TableFilter from '../../../public/assets/img/tableFilter.svg';

const DataTable = ({
	data = [],
	columns: initialColumns,
	tableName = 'Table Name',
	tableSubTitle = 'Table Sub Title',
	includeCheckbox = false,
	showTableFilter = true,
	showAddToList = false,
	//this will show add to list button
	excludeColumnKey = null,
	//this will be helpful in excluding the column from filtering
	searchplaceholder = 'Search',
	//text in search bar
	clickableColumnKey = null,
	clickableColumnKey2 = null,
	//column which can be clickabla
	onSort,
	sortConfig = { key: null, direction: 'Ascending' },
	onSearch,
	//search functionality
	onCellClick = () => { },
	noResultsText = 'No records found',
	hideSearchIfOneStoreSelected = true,
	//will hide the search bar
	uncheckTheseFilters = null,
	//this is to give the array of fields that should be unselected in table filter
	isOneStoreSelected = false,
	//check if only one store is selected
	showAvatarOnStart = false,
	sortExcludeColumns = ['customerName', 'time'],
	hideHeaders = [],
	disableButtons = false,
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedRows, setSelectedRows] = useState([]);

	const sortedInitialColumns = useMemo(() => {
		return [...initialColumns].sort((a, b) => a.tableOrder - b.tableOrder);
	}, [initialColumns]);
	const [selectedColumns, setSelectedColumns] = useState(sortedInitialColumns);

	// Key to store the selection in local storage
	const storageKey = `${tableName}-selectedColumns`;

	useEffect(() => {
		let initialSelectedColumns;	
		if (isOneStoreSelected && uncheckTheseFilters && uncheckTheseFilters.length > 0) {
			initialSelectedColumns = sortedInitialColumns
				.filter(col => !uncheckTheseFilters.includes(col.key))
				.sort((a, b) => a.tableOrder - b.tableOrder);
		} else {
			const savedSelection = sessionStorage.getItem(storageKey);
			if (savedSelection) {
				const savedKeys = JSON.parse(savedSelection);
				initialSelectedColumns = sortedInitialColumns
					.filter(col => savedKeys.includes(col.key))
					.sort((a, b) => a.tableOrder - b.tableOrder);
			} else {
				initialSelectedColumns = sortedInitialColumns
					.filter(col => !(uncheckTheseFilters && uncheckTheseFilters.includes(col.key)))
					.sort((a, b) => a.tableOrder - b.tableOrder);
			}
		}
		setSelectedColumns(initialSelectedColumns);
	}, [sortedInitialColumns, storageKey, uncheckTheseFilters, isOneStoreSelected]);

	const handleSortClick = (columnKey) => {
		if (onSort) {
			onSort(columnKey);
		}
	};

	//search functionality
	const handleSearchChange = (event) => {
		const sanitizedValue = event.target.value.replace(/[^a-zA-Z0-9-,\s]/g, '');
		setSearchTerm(sanitizedValue);
	};
	const handleKeyPress = (event) => {
		if (event.key === 'Enter' && onSearch) {
			onSearch(searchTerm);
		}
		if (!searchTerm) {
			onSearch(searchTerm);
		}
	};

	const handleSelectRow = (index) => {
		const newSelectedRows = selectedRows.includes(index) ? selectedRows.filter(rowIndex => rowIndex !== index) : [...selectedRows, index];
		setSelectedRows(newSelectedRows);
	};

	const handleColumnMenuOpen = (event) => {
		//console.log('filter cliked');
		setAnchorEl(event.currentTarget);
	};

	const handleColumnMenuClose = () => {
		setAnchorEl(null);
	};

	const handleColumnSelect = (column) => {
		const isExcluded = Array.isArray(excludeColumnKey) ? excludeColumnKey.includes(column.key) : column.key === excludeColumnKey;
		if (isExcluded) return;

		let newSelectedColumns = selectedColumns.some(selectedColumn => selectedColumn.key === column.key)
			? selectedColumns.filter(selectedColumn => selectedColumn.key !== column.key)
			: [...selectedColumns, column];

		// Re-sort the updated selectedColumns array to maintain 'tableOrder'
		newSelectedColumns = newSelectedColumns.sort((a, b) => a.tableOrder - b.tableOrder);

		setSelectedColumns(newSelectedColumns);
		sessionStorage.setItem(storageKey, JSON.stringify(newSelectedColumns.map(col => col.key)));
	};

	const handleSelectAllRows = (event) => {
		if (event.target.checked) {
			// Select all rows
			setSelectedRows(data.map((_, index) => index));
		} else {
			// Deselect all rows
			setSelectedRows([]);
		}
	};

	const toggleSelectAllColumns = () => {
		const excludedKeys = Array.isArray(excludeColumnKey) ? excludeColumnKey : [excludeColumnKey].filter(Boolean);
		const selectableColumns = sortedInitialColumns.filter(column => !excludedKeys.includes(column.key));
		// Check if all selectable columns are currently selected
		const areAllSelectableColumnsSelected = selectableColumns.every(column =>
			selectedColumns.find(selectedColumn => selectedColumn.key === column.key)
		);
		let updatedSelectedColumns;
		if (!areAllSelectableColumnsSelected) {
			// If not all selectable columns are selected, select them
			updatedSelectedColumns = [...selectableColumns, ...sortedInitialColumns.filter(column => excludedKeys.includes(column.key))];
		} else {
			// If all non-excluded columns are selected, deselect all except the excluded ones
			updatedSelectedColumns = sortedInitialColumns.filter(column => excludedKeys.includes(column.key));
		}
		// Sort the updated selectedColumns array to maintain tableOrder
		updatedSelectedColumns = updatedSelectedColumns.sort((a, b) => a.tableOrder - b.tableOrder);
		
		setSelectedColumns(updatedSelectedColumns);
		const updatedSelectedKeys = updatedSelectedColumns.map(col => col.key);
		sessionStorage.setItem(storageKey, JSON.stringify(updatedSelectedKeys));
	};

	const handleAddToListMenuOpen = () => {
		//console.log('to be implemented');
	}

	// Helper functions for formatting
	const formatDate = (isoDateString) => {
		if (!isoDateString) return 'N/A'; // Return 'N/A' if the date is null/undefined
		const date = new Date(isoDateString);
		return date.toLocaleDateString(); // Example: '12/31/2024'
	};

	const formatTime = (isoDateString) => {
		if (!isoDateString) return 'N/A'; // Return 'N/A' if the date is null/undefined
		const date = new Date(isoDateString);
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); // Example: '10:28:17 PM'
	};

	const generateActionButtons = (row, key) => {
		let actionObj = null;
		const buttonStyles = disableButtons
		? {
			color: '#605E62',
			backgroundColor: '#E4E4E4',
			cursor: 'not-allowed',
		}
		: {};
		if (String(row[key]).indexOf('|') > -1) {
			let optionsValue = String(row[key]).split('|')
			actionObj = row?.['connected'] === true ? <button className='details-class-deactive' data-testid='deactiveButton' aria-label={optionsValue[0]}
				variant='outlined' onClick={() => onCellClick(row, key)} disabled={disableButtons} style={buttonStyles}>{optionsValue[0]}</button> :
				<button className='details-class-active' data-testid='activeButton' aria-label={optionsValue[1]}
					variant='outlined' onClick={() => onCellClick(row, key)} disabled={disableButtons} style={buttonStyles}>{optionsValue[1]}</button>

		} else {
			actionObj = <button className='details-class' data-testid='defaultButton' aria-label={row[key]}
				variant='outlined' onClick={() => onCellClick(row, key)} disabled={disableButtons} style={buttonStyles}>{row[key]}</button>
		}

		return actionObj
	}

	return (
		<div className='reusable-datatable'>
			<div className='container1' >
				<div className='text-align-left'>
					<Typography variant='h6' className='table-name'>{tableName}</Typography>
					<Typography variant='subtitle2' className='table-subtitle'>{tableSubTitle}</Typography>
				</div>
				<div className='righthandsideIcons'>
					{showAddToList &&
						<IconButton
							onClick={handleAddToListMenuOpen}
							className='filter-sort-icons'>
							<MoreVertIcon sx={{ color: 'black' }} />
						</IconButton>
					}
					{showTableFilter &&
						<IconButton
							onClick={handleColumnMenuOpen}
							className='filter-sort-icons'
							data-testid='tableFilter'
						>
							<img src={TableFilter} alt='TableFilter' />
						</IconButton>
					}

					{/*Table Filter Menu:START*/}
					<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleColumnMenuClose}>
						<MenuItem onClick={toggleSelectAllColumns} sx={{ padding: '0 32px 0 8px' }}>
							<Checkbox
								checked={selectedColumns.length === initialColumns.length}
								indeterminate={selectedColumns.length > 1 && selectedColumns.length < initialColumns.length}
								// should fix this while working on add to list story
								sx={{
									color: 'black',
									'&.Mui-checked': {
										color: 'black',
									},
									'& .MuiSvgIcon-root': {
										color: 'black',
									}
								}}
							/>
							<Typography className='text-color-black' sx={{ fontSize: '14px', lineHeight: '18px' }}>Select All</Typography>
						</MenuItem>
						<div className='divider-line'></div>
						{initialColumns.map(column => (
							<MenuItem key={column.key} onClick={() => handleColumnSelect(column)} disabled={Array.isArray(excludeColumnKey) ? excludeColumnKey.includes(column.key) : column.key === excludeColumnKey} sx={{ padding: '0 32px 0 8px' }}>
								<Checkbox
									checked={selectedColumns.some(col => col.key === column.key)}
									disabled={Array.isArray(excludeColumnKey) ? excludeColumnKey.includes(column.key) : column.key === excludeColumnKey}
									// should fix this while working on add to list story
									sx={{
										color: 'black', // Color when the checkbox is unchecked
										'&.Mui-checked': {
											color: 'black', // Color when the checkbox is checked
										},
										'& .MuiSvgIcon-root': { // Targeting the check icon specifically
											color: 'black', // Ensuring the check icon is also black
										}
									}}
								/>
								<Typography className='text-color-black' sx={{ fontSize: '14px', lineHeight: '18px' }}>{column.label}</Typography>
							</MenuItem>
						))}
					</Menu>
					{/*Table Filter Menu:END*/}

				</div>
			</div>
			<div className='divider-line'></div>
			{hideSearchIfOneStoreSelected &&
				<div className='padding16 search-box'>
					<TextField
						fullWidth
						variant='outlined'
						placeholder={searchplaceholder}
						value={searchTerm}
						autoComplete='off'
						onChange={handleSearchChange}
						onKeyUp={handleKeyPress}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>
				</div>
			}
			<TableContainer component={Paper} className='table-container'>
				<Table>
					<TableHead className='table-header'>
						<TableRow>
							{includeCheckbox && (
								<TableCell padding='checkbox'>
									<Checkbox
										indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
										checked={data.length > 0 && selectedRows.length === data.length}
										onChange={handleSelectAllRows} // Adjusted for correct reference
									/>
								</TableCell>
							)}

							{showAvatarOnStart ? <TableCell></TableCell> : <></>}
							{selectedColumns.map(column => (
								<TableCell
									key={column.key}
									onClick={() => !sortExcludeColumns.includes(column.key) && handleSortClick(column.key)}
									className='table-cell-header'
									sx={{
										cursor: !sortExcludeColumns.includes(column.key) ? 'pointer' : 'default'
									}}
								>
									<div className='table-cell-header-container'>
										{!hideHeaders.includes(column.key) ? <span>{column.label}</span> : null}
										{!sortExcludeColumns.includes(column.key) && (
											<div className='table-cell-header-column'>
												{sortConfig.key === column.key ? (
													<>
														<ArrowDropUpIcon style={{ color: sortConfig.direction === 'Ascending' ? '#111111' : '#C0C0C0', fontSize: 'small' }} viewBox='0 0 16 16' />
														<ArrowDropDownIcon style={{ color: sortConfig.direction === 'Descending' ? '#111111' : '#C0C0C0', fontSize: 'small' }} viewBox='0 8 16 16' />
													</>
												) : (
													<>
														<ArrowDropUpIcon style={{ color: '#C0C0C0', fontSize: 'small' }} viewBox='0 0 16 16' />
														<ArrowDropDownIcon style={{ color: '#C0C0C0', fontSize: 'small' }} viewBox='0 8 16 16' />
													</>
												)}
											</div>
										)}
									</div>
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					<TableBody>
						{data.length > 0 ? (
							data.map((row, rowIndex) => (
								<TableRow key={rowIndex}>
									{includeCheckbox && (
										<TableCell padding='checkbox'>
											<Checkbox
												checked={selectedRows.includes(rowIndex)}
												onChange={() => handleSelectRow(rowIndex)}
											/>
										</TableCell>
									)}

									{showAvatarOnStart ? (<TableCell padding='checkbox'><PersonOutlineSharpIcon  data-testid='PersonOutlineSharpIcon' className='avatar-class' /></TableCell>) : <></>}
									{selectedColumns.map((column) => (
										<TableCell
											key={`${rowIndex}-${column.key}`}
											onClick={() => { if (column.key === clickableColumnKey) { onCellClick(row, column.key);
												} else if (column.key === clickableColumnKey2) { onCellClick(row, column.key);
												}
											  }}
											  sx={{
												cursor: (column.key === clickableColumnKey || column.key === clickableColumnKey2) ? 'pointer' : 'default',
												color: (column.key === clickableColumnKey ) ? '#00563C' : 'inherit',
												fontWeight: column.key === clickableColumnKey ? '700' : '',
											  }}
										>
											{column.key === 'orderSubTotal' || column.key === 'storeCommissionPerDate' || column.key === 'storePayoutPerDate' || column.key === 'netPayOut' || column.key === 'commission' || column.key === 'taxes'
												? (typeof row[column.key] === 'number'
													? `$${row[column.key].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
													: 'N/A')
												: column.key === 'payoutDate'
													? formatDate(row['payoutDate'])
													: column.key === 'orderDate'
														? formatDate(row['orderDate'])
														: column.key === 'time'
															? formatTime(row['orderDate'])
															: String(column.key).indexOf('action-') > -1
																? generateActionButtons(row, column.key)
																: column.key === 'storescount'
																	? row[column.key]
																	: column.key === 'status-with-inidcation'
																		? row?.['connected'] === true ? <>Activated <svg height='15' width='15' className='blinking'>
																			<circle cx='7' cy='7' r='7' fill='green' />
																		</svg> </> : row?.['connected'] === false ? <>Deactivated <svg height='15' width='15' className='blinking'>
																			<circle cx='7' cy='7' r='7' fill='red' />
																		</svg></> : <>Unknown <svg height='15' width='15' className='blinking'>
																			<circle cx='7' cy='7' r='7' fill='grey' />
																		</svg></>
																		: row[column.key] !== null && row[column.key] !== undefined
																			? row[column.key]
																			: 'N/A'}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={selectedColumns.length + (includeCheckbox ? 1 : 0)} className='text-align-center'>
									{noResultsText}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
};

export default DataTable;
